import { SchedulingFilter, DbRef, orderedDaysOfWeek, DayOfWeek } from '@caresend/types';
import { getDistanceBetweenLocations } from './location.js';
import { deduplicateArray } from '../languageExtensions/array.js';
import 'lodash-unified';

const isMobileCollectionEnabled = schedulingFilters => schedulingFilters.includes(SchedulingFilter.MOBILE_COLLECTION);
const isNurseFilterEnabled = schedulingFilters => schedulingFilters.includes(SchedulingFilter.NURSE);
const isCareSendSupplyFilterEnabled = schedulingFilters => schedulingFilters.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND);
const isOfficeSupplyFilterEnabled = schedulingFilters => schedulingFilters.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_OFFICE);
const isDropOffFilterEnabled = schedulingFilters => schedulingFilters.includes(SchedulingFilter.DROP_OFF);
const isSiteCollectionFilterEnabled = (schedulingFilters, schedulingConfig) => {
  const siteCollectionFilterPresent = schedulingFilters.includes(SchedulingFilter.SITE_COLLECTION);
  const siteCollectionEnabledByOffice = schedulingConfig.dropOffScheduling.labDrawSiteCollection;
  return siteCollectionFilterPresent && siteCollectionEnabledByOffice;
};
const getTimeSlotsByServiceRegion = async (serviceRegionID, getValueOnce) => getValueOnce(`${DbRef.TIME_SLOTS_BY_SERVICE_REGION}/${serviceRegionID}`);
/**
 * Return the patient supply owner info by ownerID by timestamp
 * given a service region group
 */

const getPatientShipmentInfoOnServiceRegionGroup = async (serviceRegionGroupID, getValueOnce) => getValueOnce(`${DbRef.PATIENT_SHIPMENT_INFO_BY_SERVICE_REGION_GROUP}/${serviceRegionGroupID}`);
/**
 * Return the lab supply shipment info by place group ID by timestamp.
 */

const getLabShipmentInfoOnServiceRegionGroup = async (serviceRegionGroupID, getValueOnce) => getValueOnce(`${DbRef.LAB_SHIPMENT_INFO_BY_SERVICE_REGION_GROUP}/${serviceRegionGroupID}`);
const getPlaceInfoByServiceRegion = async (serviceRegionID, getValueOnce) => getValueOnce(`${DbRef.PLACES_BY_SERVICE_REGION}/${serviceRegionID}`);
/**
 * Get the scheduling filter layer based on the supply instruction config options
 * by procedure. Return a single SchedulingFilter array with the filters that are
 * enabled. Each filter is enabled if the supply instruction config options
 * includes the filter, except for `SITE_COLLECTION` and `MOBILE_COLLECTION`, which
 * are only included if all the procedures have it enabled.
 *
 * However, if the `isNurseRescheduling` flag is true, we return a filter with the
 * `MOBILE_COLLECTION` and optional `DROP_OFF` filters.
 */

const getSchedulingFilters = (isNurseRescheduling, supplyInstructionConfigOptions) => {
  if (isNurseRescheduling) {
    const someInstructionHasDropOff = Object.values(supplyInstructionConfigOptions !== null && supplyInstructionConfigOptions !== void 0 ? supplyInstructionConfigOptions : {}).some(instructions => {
      var _a;

      return (_a = instructions.schedulingFilters) === null || _a === void 0 ? void 0 : _a.includes(SchedulingFilter.DROP_OFF);
    });
    const filters = [SchedulingFilter.MOBILE_COLLECTION];
    if (someInstructionHasDropOff) filters.push(SchedulingFilter.DROP_OFF);
    return filters;
  }

  const schedulingFilter = [];
  let includeSiteCollection = true;
  let includeMobileCollection = true;
  Object.entries(supplyInstructionConfigOptions !== null && supplyInstructionConfigOptions !== void 0 ? supplyInstructionConfigOptions : {}).forEach(([_, instructions]) => {
    var _a;

    const layer = Object.values((_a = instructions.schedulingFilters) !== null && _a !== void 0 ? _a : {});

    if (Array.isArray(layer)) {
      if (layer.includes(SchedulingFilter.SUPPLY) || layer.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND)) {
        schedulingFilter.push(SchedulingFilter.SUPPLIES_SHIPPED_BY_CARESEND);
      }

      if (layer.includes(SchedulingFilter.SUPPLIES_SHIPPED_BY_OFFICE)) {
        schedulingFilter.push(SchedulingFilter.SUPPLIES_SHIPPED_BY_OFFICE);
      }

      if (layer.includes(SchedulingFilter.NURSE)) {
        schedulingFilter.push(SchedulingFilter.NURSE);
      }

      if (layer.includes(SchedulingFilter.DROP_OFF)) {
        schedulingFilter.push(SchedulingFilter.DROP_OFF);
      }

      if (!layer.includes(SchedulingFilter.SITE_COLLECTION)) {
        includeSiteCollection = false;
      }

      if (!layer.includes(SchedulingFilter.MOBILE_COLLECTION)) {
        includeMobileCollection = false;
      }
    }
  });

  if (includeSiteCollection) {
    schedulingFilter.push(SchedulingFilter.SITE_COLLECTION);
  }

  if (includeMobileCollection) {
    schedulingFilter.push(SchedulingFilter.MOBILE_COLLECTION);
  }

  return deduplicateArray(schedulingFilter);
};
/**
 * Adjust the opening hours for friday if needed - controlled by the `fridayBrickAndMortarCutoffTime` variable
 * if friday's opening hours start after the cutoff time, filter out friday
 *
 * @param openingHours - The opening hours to adjust. Assumed to not have any 'overflowing' day ranges,
 * for example, no instances of Friday - Monday. Logic must be adjusted if this is not the case.
 * @param fridayCutoffTime - The cutoff time for friday.
 * @returns The adjusted opening hours
 */

const maybeAdjustFridayOpeningHours = (openingHours, fridayCutoffTime) => {
  const fridayIndex = 4;
  const adjustedOpeningHours = [];
  openingHours.forEach(openingHour => {
    const dayFromIndex = orderedDaysOfWeek.indexOf(openingHour.dayFrom);
    const dayToIndex = orderedDaysOfWeek.indexOf(openingHour.dayTo); // Do not adjust if friday isn't in range,
    // if the opening hours are before the cutoff time anyways,
    // or if the opening hours are deliberately after the cutoff time

    if (fridayIndex < dayFromIndex || fridayIndex > dayToIndex || Number(openingHour.timeTo) < fridayCutoffTime) {
      adjustedOpeningHours.push(openingHour);
      return;
    }

    const openingHoursAreAfterCutoff = Number(openingHour.timeFrom) >= fridayCutoffTime;
    const fridayHours = {
      dayFrom: DayOfWeek.FRIDAY,
      dayTo: DayOfWeek.FRIDAY,
      timeFrom: openingHour.timeFrom,
      timeTo: String(fridayCutoffTime)
    }; // If the opening hours are for friday, adjust the time to the cutoff time just for this day

    if (dayFromIndex === fridayIndex && dayToIndex === fridayIndex) {
      // If the opening hours are for friday start after our cutoff, filter out friday
      if (openingHoursAreAfterCutoff) return;
      adjustedOpeningHours.push(fridayHours);
      return;
    } // If the start or end day is friday, adjust the respective day to the next closest day
    // (thursday or saturday respectively), and add friday hours after or before respectively


    if (dayFromIndex === fridayIndex || dayToIndex === fridayIndex) {
      const dayFrom = dayFromIndex === fridayIndex ? DayOfWeek.SATURDAY : openingHour.dayFrom;
      const dayTo = dayToIndex === fridayIndex ? DayOfWeek.THURSDAY : openingHour.dayTo;
      const adjustedDays = Object.assign(Object.assign({}, openingHour), {
        dayFrom,
        dayTo
      }); // If the opening hours for friday start after our cutoff, filter out friday

      if (openingHoursAreAfterCutoff) {
        adjustedOpeningHours.push(adjustedDays);
        return;
      }

      const pushOrder = dayFrom === DayOfWeek.SATURDAY ? [fridayHours, adjustedDays] : [adjustedDays, fridayHours];
      adjustedOpeningHours.push(...pushOrder);
      return;
    } // If the opening hours have friday in BETWEEN the range, add the hours before friday with regular close
    // time, then friday with the cutoff time (unless it's after the cutoff, then filter it out),
    // then the hours after friday with regular close time


    adjustedOpeningHours.push(Object.assign(Object.assign({}, openingHour), {
      dayTo: DayOfWeek.THURSDAY
    }));
    if (openingHoursAreAfterCutoff) adjustedOpeningHours.push(fridayHours);
    adjustedOpeningHours.push(Object.assign(Object.assign({}, openingHour), {
      dayFrom: DayOfWeek.SATURDAY
    }));
  });
  return adjustedOpeningHours;
};
const getBrickAndMortarLab = (place, placeGroupName, placePhoneNumber, draftWaypointLocation) => {
  var _a;

  let distanceToAddress = 0;

  if (place.location && draftWaypointLocation) {
    distanceToAddress = getDistanceBetweenLocations(place.location, draftWaypointLocation, true);
  }

  const appointmentOnly = 'appointmentOnly' in place ? place.appointmentOnly : false;
  return Object.assign(Object.assign({}, appointmentOnly && {
    appointmentOnly
  }), {
    distanceToAddress,
    location: place.location,
    name: placeGroupName,
    openingHours: (_a = place.openingHours) !== null && _a !== void 0 ? _a : [],
    phoneNumber: placePhoneNumber,
    placeGroupID: place.placeGroupID,
    placeID: place.id,
    price: 0
  });
};
const getBrickAndMortarLabsFromPlaces = (places, placeGroups, draftWaypointLocation) => Object.values(places).reduce((labInfos, place) => {
  const placeGroup = placeGroups[place.placeGroupID];
  if (!placeGroup) return labInfos;
  const placePhoneNumber = place.contactNumber;
  const placeGroupName = placeGroup.name;
  const labInfo = getBrickAndMortarLab(place, placeGroupName, placePhoneNumber, draftWaypointLocation);
  labInfos.push(labInfo);
  return labInfos;
}, []).sort((a, b) => a.distanceToAddress - b.distanceToAddress);

export { getBrickAndMortarLab, getBrickAndMortarLabsFromPlaces, getLabShipmentInfoOnServiceRegionGroup, getPatientShipmentInfoOnServiceRegionGroup, getPlaceInfoByServiceRegion, getSchedulingFilters, getTimeSlotsByServiceRegion, isCareSendSupplyFilterEnabled, isDropOffFilterEnabled, isMobileCollectionEnabled, isNurseFilterEnabled, isOfficeSupplyFilterEnabled, isSiteCollectionFilterEnabled, maybeAdjustFridayOpeningHours };
